import React from 'react';
import { Search } from '@packages/ui/composites/Search';
import { Dropdown } from '@packages/ui/composites/Dropdown';
import { Filter } from '@packages/ui/composites/Filter';
import { Button, type ButtonVariant } from '@packages/ui/primitives/Button';
import { Icon } from '@packages/ui/primitives/Icon';
import { Typography } from '@packages/ui/primitives/Typography';
import { DataTable } from '@packages/ui/patterns/DataTable';
import type { InternalActionDef, InternalToolbarButton, FilterDef } from '../schema/types';
import type { ActionContext, PendingConfirmation } from '../../hooks/useActionExecutor';
import { ActionRegistry } from '../../registry/ActionRegistry';
import type { ActionExecutionContext } from '../../registry/ActionRegistry';
import { computeHasSelectedItems, resolveSelectedRows } from '../../utils/selectionHelpers';
import { FilterPanel } from './FilterPanel';
import { TABLE_IDS } from '../semantic-contract';
import type { EventScope } from '@packages/block-events/core';
import { mergeActionExecutionParams } from '../../utils/mergeActionExecutionParams';
import { ToolbarPrefixSlot, ToolbarSearchGroup } from './tableToolbar.styled';

const ResetFilterButton = DataTable.ResetFilterButton;

interface TableToolbarProps {
  searchEnabled: boolean;
  searchPlaceholder: string;
  searchInput: string;
  onSearchChange: (value: string) => void;
  filterDefs: readonly FilterDef[];
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  pendingFilter: Record<string, unknown>;
  handleFilterChange: (name: string, value: unknown) => void;
  handleApplyFilter: () => void;
  handleResetFilter: () => void;
  handleAsyncOptionsLoaded: (name: string, options: Array<{ label: string; value: string | number }>) => void;
  axiosInstance?: any;
  buttons: readonly InternalToolbarButton[];
  selectedKeysValue: string[];
  buildActionContext: (base: Partial<ActionContext> & { actionDef: InternalActionDef }) => ActionContext;
  executeAction: (action: InternalActionDef, context: ActionContext) => Promise<PendingConfirmation | null>;
  setPendingConfirm: (confirm: PendingConfirmation | null) => void;
  stateActions: { clearFilters: () => void };
  onFilterChange?: (filter: Record<string, unknown>) => void;
  setPendingFilter: (filter: Record<string, unknown>) => void;
  setFilterChips: (chips: Array<{ fieldName: string; label: string; displayValue: string }>) => void;
  refreshTable: () => void;
  filterGridColumns?: number;
  executingActionIds: Set<string>;
  setExecutingActionIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  isAllPagesSelected: boolean;
  uncheckedIds: string[];
  effectiveTotalCount: number;
  displayData: unknown[];
  resolveRowKey: (row: Record<string, unknown>, rowIndex: number) => string;
  buildTableQuery: () => Record<string, unknown>;
  clearSelection: () => void;
  isToolbarButtonDisabled?: (actionId: string) => boolean;
  renderToolbarSuffix?: () => React.ReactNode;
  renderToolbarPrefix?: () => React.ReactNode;
  eventScope?: EventScope | null;
  tableSemanticId?: string;
  actionAmbientParams?: Record<string, unknown>;
  tableFilters?: Record<string, unknown>;
  customFilterToolbar?: { openActionId: string } | null;
  onCustomFilterReset?: () => void;
}

export function TableToolbar({
  searchEnabled,
  searchPlaceholder,
  searchInput,
  onSearchChange,
  filterDefs,
  isFilterOpen,
  setIsFilterOpen,
  pendingFilter,
  handleFilterChange,
  handleApplyFilter,
  handleResetFilter,
  handleAsyncOptionsLoaded,
  axiosInstance,
  buttons,
  selectedKeysValue,
  buildActionContext,
  executeAction,
  setPendingConfirm,
  stateActions,
  onFilterChange,
  setPendingFilter,
  setFilterChips,
  refreshTable,
  filterGridColumns,
  executingActionIds,
  setExecutingActionIds,
  isAllPagesSelected,
  uncheckedIds,
  effectiveTotalCount,
  displayData,
  resolveRowKey,
  buildTableQuery,
  clearSelection,
  isToolbarButtonDisabled,
  renderToolbarSuffix,
  renderToolbarPrefix,
  eventScope,
  tableSemanticId,
  actionAmbientParams,
  tableFilters,
  customFilterToolbar,
  onCustomFilterReset,
}: TableToolbarProps): JSX.Element {
  const oopActionParams = mergeActionExecutionParams({ tableFilters, ambient: actionAmbientParams });
  const hasSelection = computeHasSelectedItems({
    selectedKeys: selectedKeysValue,
    isAllPagesSelected,
    uncheckedIds,
    totalCount: effectiveTotalCount,
  });

  const runToolbarOopAction = async (actionId: string) => {
    if (executingActionIds.has(actionId)) return;
    setExecutingActionIds((prev) => new Set(prev).add(actionId));
    try {
      if (ActionRegistry.hasAction(actionId)) {
        const execCtx: ActionExecutionContext = {
          pk: '',
          row: {},
          selectedKeys: selectedKeysValue,
          selectedRows: isAllPagesSelected
            ? undefined
            : resolveSelectedRows(selectedKeysValue, displayData, resolveRowKey),
          tableQuery: buildTableQuery(),
          refreshTable,
          clearSelection,
          selectAllActive: isAllPagesSelected,
          deselectedRowIds: isAllPagesSelected ? uncheckedIds : undefined,
          totalCount: isAllPagesSelected ? effectiveTotalCount : undefined,
          axiosInstance,
          eventScope: eventScope ?? undefined,
          tableSemanticId,
          params: oopActionParams,
        };
        await ActionRegistry.executeAction(actionId, execCtx);
      }
    } finally {
      setExecutingActionIds((prev) => {
        const next = new Set(prev);
        next.delete(actionId);
        return next;
      });
    }
  };
  const hasToolbarPrefix = renderToolbarPrefix != null;
  const leftToolbarLayoutClass = hasToolbarPrefix
    ? 'flex flex-wrap items-start gap-2 gap-x-4 w-full sm:col-span-1 lg:col-span-2'
    : 'flex flex-wrap items-start gap-2 w-full sm:col-span-1 lg:col-span-2 sm:grid sm:grid-cols-2 sm:gap-4';

  return (
    <div
      id={TABLE_IDS.toolbar}
      className='flex flex-wrap items-start gap-2 mb-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:mb-6 lg:grid-cols-4 lg:mb-8'
      data-testid='dt-v2-toolbar'>
      <div className={leftToolbarLayoutClass}>
        {hasToolbarPrefix && renderToolbarPrefix != null ? (
          <ToolbarSearchGroup
            $attached={searchEnabled}
            className='shrink-0'>
            <ToolbarPrefixSlot data-dt-v2-toolbar-prefix>
              {renderToolbarPrefix()}
            </ToolbarPrefixSlot>
            {searchEnabled && (
              <div className='custom-search min-w-0 flex-1'>
                <Search
                  placeholder={searchPlaceholder}
                  value={searchInput}
                  onValueChange={(value: string) => onSearchChange(value)}
                  onSearch={(value: string) => onSearchChange(value)}
                  onClear={() => onSearchChange('')}
                  inputId={TABLE_IDS.search}
                  semanticId={TABLE_IDS.search}
                />
              </div>
            )}
          </ToolbarSearchGroup>
        ) : (
          searchEnabled && (
            <div className='custom-search min-w-0 flex-1 sm:col-span-1'>
              <Search
                placeholder={searchPlaceholder}
                value={searchInput}
                onValueChange={(value: string) => onSearchChange(value)}
                onSearch={(value: string) => onSearchChange(value)}
                onClear={() => onSearchChange('')}
                inputId={TABLE_IDS.search}
                semanticId={TABLE_IDS.search}
              />
            </div>
          )
        )}
        {filterDefs.length === 0 && customFilterToolbar != null && (
          <div className='flex items-start gap-2 shrink-0'>
            <Filter.Root open={false} onOpenChange={() => undefined}>
              <Filter.Trigger
                semanticId={TABLE_IDS.filterTrigger}
                disabled={executingActionIds.has(customFilterToolbar.openActionId)}
                onClick={(event) => {
                  event.preventDefault();
                  void runToolbarOopAction(customFilterToolbar.openActionId);
                }}
              />
            </Filter.Root>
            <div id={TABLE_IDS.filterReset}>
              <ResetFilterButton
                type='button'
                onClick={() => {
                  onCustomFilterReset?.();
                }}>
                <Icon name='settings_backup_restore' size='md' />
                <Typography variant='body-s' data-reset-label style={{ whiteSpace: 'nowrap' }}>
                  Reset Filter
                </Typography>
              </ResetFilterButton>
            </div>
          </div>
        )}
        {filterDefs.length > 0 && (
          <div className='flex items-start gap-2 shrink-0'>
            <FilterPanel
              filterDefs={filterDefs}
              isFilterOpen={isFilterOpen}
              setIsFilterOpen={setIsFilterOpen}
              pendingFilter={pendingFilter}
              handleFilterChange={handleFilterChange}
              handleApplyFilter={handleApplyFilter}
              handleAsyncOptionsLoaded={handleAsyncOptionsLoaded}
              axiosInstance={axiosInstance}
              filterGridColumns={filterGridColumns}
            />
            <div id={TABLE_IDS.filterReset}>
              <ResetFilterButton
                type='button'
                onClick={handleResetFilter}>
                <Icon name='settings_backup_restore' size='md' />
                <Typography variant='body-s' data-reset-label style={{ whiteSpace: 'nowrap' }}>
                  Reset Filter
                </Typography>
              </ResetFilterButton>
            </div>
          </div>
        )}
      </div>
      {(() => {
        const visibleButtons = buttons.filter((b) => {
          const oopAction = ActionRegistry.resolveAction(b.id);
          if (oopAction) return oopAction.isAvailable();
          return b.actionType !== 'export' && b.actionType !== 'import';
        });
        const singleButton = visibleButtons.length <= 1;

        const mapVariantToDS = (v: InternalToolbarButton['variant']): ButtonVariant => {
          switch (v) {
            case 'primary':
              return 'primary';
            case 'primary-outline':
              return 'primary-outline';
            case 'secondary-white':
              return 'secondary-white';
            case 'danger':
              return 'primary-danger';
            case 'ghost':
              return 'secondary';
            case 'secondary':
            default:
              return 'secondary';
          }
        };

        return (
          <div
            id='fmvAndActionsContainer'
            className={`flex flex-wrap items-center gap-x-4 sm:justify-end sm:col-span-1 lg:col-span-2 ${singleButton ? 'basis-full sm:basis-auto' : ''}`}>
            {renderToolbarSuffix?.()}
            <div id='actionButtonsContainer' className='flex flex-wrap items-center gap-x-4 gap-y-2'>
              {visibleButtons.map((btn, idx) => {
                const isSelectionGated = btn.requiresSelection && !hasSelection;
                const isCustomDisabled = isToolbarButtonDisabled?.(btn.id) ?? false;
                const isExecuting = executingActionIds.has(btn.id);
                const isPrimary = btn.variant === 'primary';
                const hasDropdown =
                  btn.actionType === 'dropdown' &&
                  Array.isArray(btn.options) &&
                  btn.options.length > 0;
                const dropdownOptions = hasDropdown ? btn.options ?? [] : [];
                const isDisabled = isSelectionGated || isCustomDisabled || isExecuting;
                return (
                  <div key={btn.id} className={singleButton && idx === 0 ? 'basis-full sm:basis-auto' : ''}>
                    {hasDropdown ? (
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <Button
                            variant={mapVariantToDS(btn.variant)}
                            label={btn.label}
                            type='button'
                            icon={btn.icon}
                            trailingIcon='keyboard_arrow_down'
                            loading={isExecuting}
                            disabled={isDisabled}
                            style={singleButton && idx === 0 ? { width: '100%' } : undefined}
                            semanticId={isPrimary ? TABLE_IDS.toolbarPrimary : TABLE_IDS.toolbarSecondary(btn.id)}
                          />
                        </Dropdown.Trigger>
                        <Dropdown.Content>
                          {dropdownOptions.map((opt) => (
                            <Dropdown.Item
                              key={`${btn.id}-${opt.actionId ?? opt.path}`}
                              onSelect={async () => {
                                if (opt.actionId && ActionRegistry.hasAction(opt.actionId)) {
                                  const execCtx: ActionExecutionContext = {
                                    pk: '',
                                    row: {},
                                    selectedKeys: selectedKeysValue,
                                    selectedRows: isAllPagesSelected
                                      ? undefined
                                      : resolveSelectedRows(selectedKeysValue, displayData, resolveRowKey),
                                    tableQuery: buildTableQuery(),
                                    refreshTable,
                                    clearSelection,
                                    selectAllActive: isAllPagesSelected,
                                    deselectedRowIds: isAllPagesSelected ? uncheckedIds : undefined,
                                    totalCount: isAllPagesSelected ? effectiveTotalCount : undefined,
                                    axiosInstance,
                                    eventScope: eventScope ?? undefined,
                                    tableSemanticId,
                                    params: oopActionParams,
                                  };
                                  await ActionRegistry.executeAction(opt.actionId, execCtx);
                                  return;
                                }
                                const navigate = ActionRegistry.getNavigate();
                                if (opt.path && navigate) {
                                  navigate(opt.path);
                                }
                              }}>
                              {opt.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Content>
                      </Dropdown.Root>
                    ) : (
                      <Button
                        variant={mapVariantToDS(btn.variant)}
                        label={btn.label}
                        type='button'
                        icon={btn.icon}
                        loading={isExecuting}
                        disabled={isDisabled}
                        style={singleButton && idx === 0 ? { width: '100%' } : undefined}
                        onClick={async () => {
                          if (isExecuting) return;
                          setExecutingActionIds((prev) => new Set(prev).add(btn.id));
                          try {
                            if (ActionRegistry.hasAction(btn.id)) {
                              const execCtx: ActionExecutionContext = {
                                pk: '',
                                row: {},
                                selectedKeys: selectedKeysValue,
                                selectedRows: isAllPagesSelected
                                  ? undefined
                                  : resolveSelectedRows(selectedKeysValue, displayData, resolveRowKey),
                                tableQuery: buildTableQuery(),
                                refreshTable,
                                clearSelection,
                                selectAllActive: isAllPagesSelected,
                                deselectedRowIds: isAllPagesSelected ? uncheckedIds : undefined,
                                totalCount: isAllPagesSelected ? effectiveTotalCount : undefined,
                                axiosInstance,
                                eventScope: eventScope ?? undefined,
                                tableSemanticId,
                                params: oopActionParams,
                              };
                              await ActionRegistry.executeAction(btn.id, execCtx);
                              return;
                            }
                            const context = buildActionContext({
                              selectedKeys: selectedKeysValue,
                              actionDef: btn as unknown as InternalActionDef,
                            });
                            const confirmation = await executeAction(btn as unknown as InternalActionDef, context);
                            if (confirmation) setPendingConfirm(confirmation);
                          } finally {
                            setExecutingActionIds((prev) => {
                              const next = new Set(prev);
                              next.delete(btn.id);
                              return next;
                            });
                          }
                        }}
                        semanticId={isPrimary ? TABLE_IDS.toolbarPrimary : TABLE_IDS.toolbarSecondary(btn.id)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
